/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// Internal dependencies
import { preventConcurrentExec } from '@rakuten/common-util/promise';
import {
  WHITELISTED_KEYS_ANDROID,
  WHITELISTED_KEYS_IOS,
  WHITELISTED_KEYS_PLACEHOLDERS,
  WHITELISTED_KEYS_SHARED,
  WHITELISTED_KEYS_WEB
} from "./remote-config.constants";

// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

// Tag for log output etc.
const TAG = '[service/branding/remoteConfig]';

// -----------------------------------------------------------------------------
// SERVICE REGISTRATION
// -----------------------------------------------------------------------------

//service config for service locator
export const config = ["remoteConfig", [
  "fetch",
  "localConfig",
  "logger",
  "messageBus",
  "platform",
  "queryString",
  "sessionStorage?"
], createRemoteConfigService];

// -----------------------------------------------------------------------------
// PRIVATE MODULE METHODS
// -----------------------------------------------------------------------------

/**
 * Sanitizes the passed value, i.e. trims any whitespace for strings and
 * converts strings to proper JavaScript types, if possible. This is necessary
 * because values returned from the backend are strings instead of, for example,
 * boolean values or numbers.
 *
 * @param {any} value A value to sanitize
 * @returns {any} The sanitized value
 */
function sanitizeValue(value) {
  // Remove any whitespaces first
  value = value.constructor === String ? value.trim() : value;

  // Check for null values
  if (value.toLowerCase() === 'null') {
    value = JSON.parse(value);

    // Check for boolean values
  } else if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
    value = JSON.parse(value);
  }

  // Check for numeric values
  else if (!isNaN(value) && !isNaN(parseFloat(value))) {
    value = Number(value);
  }

  return value;
};


/**
 * Applies configuration values to use OAuth with the tolino OAuth simulator
 *
 * @param {object} config The remote config to patch for OAuth simulator
 * @param {object} localConfig The application configuration
 * @param {object} platform Platform service
 * @return {object} The patched remote config
 */
function applyOAuthSimulatorConfig(config, localConfig, platform) {
  const simConfig = localConfig.OAUTH.SIMULATOR;

  if (platform.isWeb()) {
    simConfig.URL_OAUTH_REDIRECT = simConfig.URL_OAUTH_REDIRECT_WEB
  } else {
    simConfig.URL_OAUTH_REDIRECT = simConfig.URL_OAUTH_REDIRECT_NATIVE
  }
  delete simConfig.URL_OAUTH_REDIRECT_WEB;
  delete simConfig.URL_OAUTH_REDIRECT_NATIVE;

  return Object.assign({}, config, simConfig);
};

/**
 * Parses the config returned from backend. It strips out all unneeded key-value
 * pairs, sanitizes the values and adds some meta information about the
 * config.
 *
 * @param {object} config The config returned from backend
 * @returns {object} The processed/parsed config with only the key-values needed
 */
function parseConfig(config, platform, localConfig, queryStringService) {
  //check if this is a oauth sim request
  const isOauthSim = Boolean(queryStringService
    .getQueryParameter(localConfig.CONSTANTS.REMOTE_CONFIG.KEY_OAUTH_SIM_ENABLED));

  let result = {
    meta: {
      client_type: config.client_type,
      isOauthSim,
      reseller_id: config.reseller_id,
      last_modified: config.lastModified
    }
  };
  let whitelist;

  // Only use whitelisted keys
  if (platform === platform.platforms.ANDROID) {
    whitelist = WHITELISTED_KEYS_SHARED.concat(WHITELISTED_KEYS_ANDROID);
  } else if (platform === platform.platforms.IOS) {
    whitelist = WHITELISTED_KEYS_SHARED.concat(WHITELISTED_KEYS_IOS);
  } else {
    whitelist = WHITELISTED_KEYS_SHARED.concat(WHITELISTED_KEYS_WEB);
  }
  Object.keys(config.config).forEach(key => {
    if (
      whitelist.indexOf(key) > -1 ||
      WHITELISTED_KEYS_PLACEHOLDERS.findIndex(
        placeholder => key.startsWith(placeholder)
      ) > -1
    ) {
      result[key] = sanitizeValue(config.config[key]);
    }
  });

  // For local development, we use an OAuth simulator to be able to work in a
  // logged-in state
  // TODO: Make this configurable so that a) not all development builds use the
  //       simulator and b) production builds may use the simulator.
  if (localConfig.BUNDLER.BUILD_MODE === localConfig.BUNDLER.BUILD_MODES.DEVELOPMENT || isOauthSim) {
    result = applyOAuthSimulatorConfig(result, localConfig, platform);
  }

  return result;
};

// -----------------------------------------------------------------------------
// REMOTE CONFIG SERVICE
// -----------------------------------------------------------------------------

/**
 * Factory function which creates a oauth service with given
 * depedencies
 */
export function createRemoteConfigService(
  fetchService,
  localConfigService,
  loggerService,
  messageBusService,
  platformService,
  queryStringService,
  sessionStorageService
) {

  const appConfig = localConfigService.getLocalConfig();

  // Prevent multiple calls to functions that create the same promise
  const guardedPromise = preventConcurrentExec();

  // A map of remote configurations
  let remoteConfig = {};

  // ---------------------------------------------------------------------------
  // PRIVATE METHODS
  // ---------------------------------------------------------------------------

  /**
   * Fetch function to fetch remote config
   */
  function fetchRequest(resellerId) {
    const appId = appConfig.APPLICATION.APP_ID;

    const url = `${appConfig.NETWORK.API_BASE_URL}/v1/` +
      `resellers/${resellerId}/` +
      `client-types/${appConfig.APP_IDENTIFIERS_CLIENT_TYPES[appId] || appConfig.NETWORK.CLIENT_TYPE}/config`;

    return fetchService.fetch({
      url,
      options: {
        method: 'GET',
        headers: {
          'reseller_id': resellerId,
          'client_type': appConfig.APP_IDENTIFIERS_CLIENT_TYPES[appId] || appConfig.NETWORK.CLIENT_TYPE,
          'client_version': appConfig.NETWORK.RESELLER_CONFIG_VERSION
        },
        responseType: 'json'
      },
      timout: 10000
    })
      .then(onResponse)
  }

  /**
   * Parses the given reseller response
   */
  function onResponse(response) {

    // We only use the keys that are whitelisted for the current platform
    // and because all values returned from backend are strings, we need to
    // sanitize and convert them.
    const parsedConfig = parseConfig(response, platformService, appConfig, queryStringService);
    remoteConfig[parsedConfig.meta.reseller_id] = parsedConfig;
    return Promise.resolve(parsedConfig);
  }

  /**
   * Retrieve a configuration for the specified resellerID from the backend.
   *
   * @param {boolean} [forceUpdate=false] Whether or not to force loading the config
   * @returns {Promise<object|null>} The reseller's config
   */
  function fetchRemoteConfig(resellerId, forceUpdate = false) {
    return guardedPromise(`${TAG}:fetchRemoteConfig`, () => {

      if (remoteConfig[resellerId] && forceUpdate === false) {
        return Promise.resolve(remoteConfig[resellerId]);
      }

      //if app was started via authapp the remote config should
      //already be stored in session config
      const sessionConfig = sessionStorageService
        ?.getItem(appConfig.CONSTANTS.REMOTE_CONFIG.KEY_AUTH_APP_REMOTE_CONFIG);

      if (sessionConfig) {
        try {
          const parsedConfig = JSON.parse(sessionConfig);
          const parsedResellerId = parsedConfig?.meta?.reseller_id;
          if(parsedResellerId){
            remoteConfig[parsedResellerId] = parsedConfig;
            return Promise.resolve(parsedConfig);
          }
        } catch (e) {
          loggerService.error(`${TAG}:fetchRemoteConfig:${e}`)
        }
      }

      // If we are requested to fetch a reseller configuration but no
      // resellerId has been passed, the application needs to know
      // about this and probably show some sort of reseller selection.
      // @FIXME This is not ideal, because this way, we tightly
      //        couple the service with the app, which makes it hard
      //        to use the service in some other context
      if (!resellerId) {
        return new Promise((resolve, reject) => messageBusService.publish(
          appConfig.MESSAGE_BUS.CHANNEL.SERVICE,
          appConfig.MESSAGE_BUS.MESSAGE.SERVICE_REMOTECONFIG_NOCONFIG,
          { resolve, reject }
        ))
          .then(resellerId => fetchRequest(resellerId));

      } else {
        // We try to load the configuration from the backend. If that is
        // not successful, e.g. the user is offline or some other
        // network mishap happend, we try to use a previously stored
        // config.
        return fetchRequest(resellerId)
        .catch((e) => {

          if(remoteConfig[resellerId]){
            return remoteConfig[resellerId];
          }

          loggerService.error(`${TAG} Not able to fetch config from remote`, e);
          return Promise.reject(`${TAG} Not able to fetch config from remote`);
        })
      }
    })
  };


  // ---------------------------------------------------------------------------
  // PUBLIC API
  // ---------------------------------------------------------------------------

  return {
    fetchRemoteConfig
  };

}
